<template>
  <div class="auth-view min-h-screen flex items-center justify-center py-8 mb-6">
    <div class="bg-white w-full max-w-xl mx-auto p-8 rounded-lg shadow-lg max-h-screen overflow-y-auto">
      <div>
        <Logo
          :path="locationLogo || defaultLogo"
          className="logo flex justify-center mb-16"
          width="160"
        ></Logo>
      </div>
      <h3 id="title" class="mt-5 text-darkBlue font-semibold text-xl">
        REGISTER
      </h3>
      <form class="mt-5" @submit.prevent="handleSubmitRegister">
        <div class="form__div">
          <input
            class="form__input"
            type="text"
            placeholder=" "
            id="firstName"
            v-model="firstName"
            required
          />
          <label for="firstName" class="form__label">First name</label>
        </div>
        <div class="form__div">
          <input
            class="form__input"
            type="text"
            placeholder=" "
            id="lastName"
            v-model="lastName"
            required
          />
          <label for="lastName" class="form__label">Last name</label>
        </div>
        <div class="form__div">
          <input
            class="form__input"
            type="email"
            id="email"
            placeholder=" "
            v-model="email"
            required
          />
          <label for="email" class="form__label">Email</label>
        </div>
        <PhoneInput
          v-model="phoneNumber"
          :model-value="phoneNumber"
          @onValidation="handlePhoneValidation"
        />
        <div class="form__div">
          <input
            class="form__input"
            type="password"
            placeholder=" "
            id="password"
            @click="changeShowPasswordReq()"
            @focus="changeShowPasswordReq()"
            v-model="password"
            minlength="8"
            required
          />
          <label for="password" class="form__label">Password</label>
          <div
            v-if="showPasswordReq"
            class="absolute right-0 top-0 p-4 border-2 bg-white shadow-lg z-20"
            style="width: 250px"
          >
            <span
              class="absolute top-4 w-4 h-4 transform rotate-45 border-l-2 border-t-2 z-10 bg-white"
              style="left: -9px"
            ></span>
            <p>To make your password stronger:</p>
            <ol class="list-disc list-inside m-2">
              <li>Min 8 characters</li>
              <li>Min 1 number</li>
              <li>Min 1 capital letter</li>
              <li>Min 1 special character</li>
            </ol>
          </div>
        </div>
        <div class="form__div">
          <input
            class="form__input"
            type="password"
            placeholder=" "
            id="confirm-password"
            v-model="confirmPassword"
            required
            minlength="8"
          />
          <label for="confirm-password" class="form__label"
            >Confirm password</label
          >
        </div>
        
        <button
          id="b-signup"
          class="text-darkBlue w-full p-3 border-0 bg-mindaro font-semibold"
          type="submit"
          :disabled="proccessingSignup"
        >
          <em v-if="proccessingSignup" class="fa fa-spinner fa-spin"></em>
          Sign Up
        </button>
      </form>
      <div class="text-center text-color m-0 mt-4">
        Already have an account? |
        <router-link :to="{ name: 'Login' }" class="underline"
          >Login</router-link
        >
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import jwt_decode from "jwt-decode";
import Logo from "@/components/auth/Logo";
import { useStore } from "vuex";
import { useToast } from "vue-toastification";

import { triggerFormSubmissionEvent } from "@/services/gtmTracker/gtmTracker.js";

import PhoneInput from "@/components/general/PhoneInput.vue";

const store = useStore();
const route = useRoute();
const router = useRouter();
const toast = useToast();

const registerNewUser = async (data) =>
  store.dispatch("user/registerNewUser", data);

const login = async (email, password) =>
  store.dispatch("user/login", { email, password });

const setUserIsLogged = (value) => store.commit("user/setUserIsLogged", value);

const firstName = ref(null);
const lastName = ref(null);
const email = ref(null);
const phoneNumber = ref("");
const password = ref(null);
const confirmPassword = ref(null);
const registrationCode = ref("");
const isSponsorAccount = ref(false);
const profiles = ref(0);
const showPasswordReq = ref(false);
const proccessingSignup = ref(false);
const phoneNumberValid = ref(false);
const phoneNumberError = ref("");
const defaultLogo = ref(require("@/assets/brain-logo.png"));

const locationLogo = computed(() => store.getters["location/getLocationLogo"]);

const appName = computed(() => store.getters["location/getAppName"]);

onMounted(() => {
  const { data } = route.query;

  if (data) {
    const decoded = jwt_decode(data);
    email.value = decoded.email;
    registrationCode.value = decoded.code;
    isSponsorAccount.value = decoded.sponsor;
    profiles.value = decoded.dependents;

    store.commit("location/setLocationName", decoded.location_name);
    store.commit("location/setLocationLogo", decoded.logo_url);
  }
});

watch(password, (newPassword) => {
  const reg = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[#?!@$ %^&*-.]).{8,}$/;
  if (reg.test(newPassword)) {
    showPasswordReq.value = false;
  }
});

function changeShowPasswordReq() {
  showPasswordReq.value = true;
}

async function handleSubmitRegister() {
  if (validatePassword()) {
    if (!phoneNumber.value) {
      return toast.error("Phone number is required");
    }

    if (!phoneNumberValid.value) {
      return toast.error(
        phoneNumberError.value || "Please enter a valid phone number"
      );
    }

    proccessingSignup.value = true;

    try {
      await registerNewUser({
        firstName: firstName.value,
        lastName: lastName.value,
        email: email.value,
        password: password.value,
        registrationCode: registrationCode.value,
        dependents: profiles.value,
        fromWeb: true,
        phoneNumber: phoneNumber.value,
      });

      if (isSponsorAccount.value) {
        sessionStorage.setItem("parentEmail", email.value);
        triggerFormSubmissionEvent(
          firstName.value,
          lastName.value,
          email.value,
          phoneNumber.value
        );

        return router.push({
          name: "Register players",
          query: {
            n: profiles.value,
            registrationMethod: "pre-pay",
          },
        });
      }

      await login(email.value, password.value);
      setUserIsLogged(true);
      return router.push({ name: "Home" });
    } catch (error) {
      proccessingSignup.value = false;
      showError(error.message);
    }
  }

  function validatePassword() {
    if (password.value !== confirmPassword.value) {
      showError("Passwords do not match.");
      return false;
    }
    if (password.value.length < 8) {
      showError(
        "This password is too short. It must contain at least 8 characters."
      );
      return false;
    }

    const reg = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[#?!@$ %^&*-.]).{8,}$/;
    if (!reg.test(password.value)) {
      showError("Password does not satisfy the requirements.");
      return false;
    }
    return true;
  }

  function showError(errorMess) {
    toast.error(errorMess);
  }
}

function handlePhoneValidation(isValid) {
  phoneNumberValid.value = isValid;
  phoneNumberError.value =
    !isValid && phoneNumber.value ? "Please enter a valid phone number" : "";
}
</script>

<style scoped>
.auth-view {
  background-image: url("../../assets/background-login-white-label.png");
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

#b-signup:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

#b-signup {
  background-color: #edfe86;
  border: none;
  padding: 11px;
  width: 100%;
  cursor: pointer;
}
</style>
